import { useQuery } from '@tanstack/react-query'
import { Ban, BookOpenText, Printer, Undo2, Wallet } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router'
import { ApiError } from '@/api/errors'
import { ErrorState, LoadingState, NotFoundState } from '@/components/feedback/States'
import { toast } from '@/components/feedback/toast-store'
import { Alert } from '@/components/ui/Alert'
import { Badge } from '@/components/ui/Badge'
import { Button, IconButton } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { DescriptionList } from '@/components/ui/DescriptionList'
import { PageHeader } from '@/components/ui/PageHeader'
import { Can } from '@/features/auth/guards'
import { useAuth } from '@/features/auth/useAuth'
import { invoiceQuery, useCancelInvoice, useReversePayment } from '@/features/finance/api'
import { InvoiceStatusBadge } from '@/features/finance/components/InvoiceStatusBadge'
import { ReasonDialog } from '@/features/finance/components/ReasonDialog'
import { RecordPaymentDialog } from '@/features/finance/components/RecordPaymentDialog'
import { frequencyLabel, localizePeriodLabel, methodLabel } from '@/features/finance/status'
import type { Payment } from '@/features/finance/types'
import { useMoney } from '@/features/finance/useCurrency'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { cn } from '@/utils/cn'
import { formatDate, formatDateTime } from '@/utils/format'

export function InvoiceDetailPage() {
  const { t } = useTranslation('finance')
  const id = Number(useParams().id)
  const { data, isPending, error, refetch } = useQuery(invoiceQuery(id))
  const { money } = useMoney()
  const { can } = useAuth()
  const cancel = useCancelInvoice()
  const reverse = useReversePayment()
  const [paying, setPaying] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [reversing, setReversing] = useState<Payment | null>(null)
  useDocumentTitle(data?.invoice.number ?? 'Invoice')

  if (isPending) return <LoadingState />
  if (error)
    return error instanceof ApiError && error.status === 404 ? (
      <NotFoundState resource="invoice" homeHref="/admin/finance/invoices" />
    ) : (
      <ErrorState onRetry={() => void refetch()} />
    )

  const inv = data.invoice
  const open = inv.status !== 'cancelled' && inv.outstanding !== '0.00'
  const paidPct =
    Number(inv.total) > 0 ? Math.min(100, (Number(inv.amountPaid) / Number(inv.total)) * 100) : 100

  return (
    <>
      <PageHeader
        title={inv.number}
        description={`${localizePeriodLabel(inv.periodLabel)} · ${inv.academicYear}`}
        breadcrumbs={[
          { label: t('Finance') },
          { label: t('Invoices'), to: '/admin/finance/invoices' },
          { label: inv.number },
        ]}
        homeHref="/admin"
        meta={
          <>
            <InvoiceStatusBadge status={inv.status} size="md" />
            {data.issuedBy ? (
              <span className="text-xs text-muted">{t('Issued by {{name}}', { name: data.issuedBy })}</span>
            ) : null}
          </>
        }
        actions={
          <>
            <Button asChild variant="secondary" leftIcon={<BookOpenText />}>
              <Link to={`/admin/finance/students/${inv.student.id}/ledger`}>{t('Student ledger')}</Link>
            </Button>
            <Can permission="finance.manage">
              {data.canCancel ? (
                <Button variant="danger-soft" leftIcon={<Ban />} onClick={() => setCancelling(true)}>
                  {t('Cancel invoice')}
                </Button>
              ) : null}
              {open ? (
                <Button leftIcon={<Wallet />} onClick={() => setPaying(true)}>
                  {t('Record payment')}
                </Button>
              ) : null}
            </Can>
          </>
        }
      />

      {data.cancellation ? (
        <Alert tone="warning" title={t('This invoice was cancelled')} className="mb-6">
          {formatDateTime(data.cancellation.at)}
          {data.cancellation.by ? ` ${t('by {{name}}', { name: data.cancellation.by })}` : ''}
          {data.cancellation.reason ? ` — ${data.cancellation.reason}` : ''}
        </Alert>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="space-y-6">
          <Card>
            <CardHeader
              title={t('Fee lines')}
              description={t('Amounts are a snapshot taken when the invoice was issued.')}
            />
            <div className="scrollbar-thin overflow-x-auto">
              <table className="w-full text-sm">
                <caption className="sr-only">{t('Invoice lines')}</caption>
                <thead>
                  <tr className="border-b border-line bg-surface-muted text-2xs tracking-wider text-muted uppercase">
                    <th scope="col" className="px-5 py-2.5 text-left font-semibold">
                      {t('Item')}
                    </th>
                    <th scope="col" className="hidden px-4 py-2.5 text-left font-semibold sm:table-cell">
                      {t('Frequency')}
                    </th>
                    <th scope="col" className="px-4 py-2.5 text-right font-semibold">
                      {t('Amount')}
                    </th>
                    <th scope="col" className="hidden px-4 py-2.5 text-right font-semibold sm:table-cell">
                      {t('Discount')}
                    </th>
                    <th scope="col" className="px-5 py-2.5 text-right font-semibold">
                      {t('Net')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.lines.map((l) => (
                    <tr key={l.id} className="border-b border-line last:border-b-0">
                      <td className="px-5 py-3">
                        <span className="block font-medium text-ink">{l.name}</span>
                        <span className="block text-xs text-muted">{l.category}</span>
                      </td>
                      <td className="hidden px-4 py-3 text-ink-soft sm:table-cell">
                        {t(frequencyLabel[l.frequency])}
                      </td>
                      <td className="px-4 py-3 text-right tabular">{money(l.amount)}</td>
                      <td className="hidden px-4 py-3 text-right text-ink-soft tabular sm:table-cell">
                        {l.discountAmount !== '0.00' ? (
                          <span title={l.discountName ?? undefined}>−{money(l.discountAmount)}</span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-5 py-3 text-right font-medium text-ink tabular">
                        {money(l.netAmount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="text-sm">
                  <tr className="border-t border-line">
                    <th
                      scope="row"
                      colSpan={2}
                      className="hidden px-5 pt-3 text-left font-normal text-muted sm:table-cell"
                    >
                      {t('Subtotal')}
                    </th>
                    <th scope="row" className="px-5 pt-3 text-left font-normal text-muted sm:hidden">
                      {t('Subtotal')}
                    </th>
                    <td colSpan={3} className="px-5 pt-3 text-right tabular">
                      {money(inv.subtotal)}
                    </td>
                  </tr>
                  {inv.discountTotal !== '0.00' ? (
                    <tr>
                      <th scope="row" colSpan={2} className="px-5 pt-1 text-left font-normal text-muted">
                        {t('Discounts')}
                      </th>
                      <td colSpan={3} className="px-5 pt-1 text-right tabular">
                        −{money(inv.discountTotal)}
                      </td>
                    </tr>
                  ) : null}
                  <tr>
                    <th scope="row" colSpan={2} className="px-5 pt-1 pb-4 text-left font-semibold text-ink">
                      {t('Total')}
                    </th>
                    <td
                      colSpan={3}
                      className="px-5 pt-1 pb-4 text-right text-base font-semibold text-ink tabular"
                    >
                      {money(inv.total)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>

          <Card>
            <CardHeader
              title={t('Payments')}
              description={
                data.payments.length
                  ? t('{{count}} recorded', { count: data.payments.length })
                  : t('No payments yet')
              }
              icon={<Wallet />}
            />
            {data.payments.length === 0 ? (
              <CardBody>
                <p className="text-sm text-muted">{t('Nothing has been paid on this invoice.')}</p>
              </CardBody>
            ) : (
              <ul className="divide-y divide-line">
                {data.payments.map((p) => (
                  <li
                    key={p.id}
                    className={cn(
                      'flex flex-wrap items-center gap-3 px-5 py-3',
                      p.isReversed && 'bg-surface-muted/60',
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-ink">
                        <span className={cn('font-mono text-xs', p.isReversed && 'line-through')}>
                          {p.receiptNumber}
                        </span>
                        <Badge size="sm">{t(methodLabel[p.method])}</Badge>
                        {p.isReversed ? (
                          <Badge size="sm" tone="danger">
                            {t('Reversed')}
                          </Badge>
                        ) : null}
                      </p>
                      <p className="mt-0.5 text-xs text-muted">
                        {formatDate(p.paidOn)}
                        {p.reference ? ` · ${t('Ref {{reference}}', { reference: p.reference })}` : ''}
                        {p.receivedBy ? ` · ${t('by {{name}}', { name: p.receivedBy })}` : ''}
                      </p>
                      {p.isReversed ? (
                        <p className="mt-0.5 text-xs text-danger-700">
                          {t('Reversed {{date}}', { date: formatDateTime(p.reversedAt) })}
                          {p.reversedBy ? ` ${t('by {{name}}', { name: p.reversedBy })}` : ''}:{' '}
                          {p.reversalReason}
                        </p>
                      ) : null}
                    </div>
                    <span
                      className={cn(
                        'text-sm font-semibold tabular',
                        p.isReversed ? 'text-muted line-through' : 'text-ink',
                      )}
                    >
                      {money(p.amount)}
                    </span>
                    <div className="flex items-center gap-1">
                      <Button
                        asChild
                        variant="ghost"
                        size="icon"
                        aria-label={t('Print receipt {{number}}', { number: p.receiptNumber })}
                        title={t('Print receipt')}
                      >
                        <Link to={`/admin/finance/payments/${p.id}/receipt`}>
                          <Printer />
                        </Link>
                      </Button>
                      {can('finance.manage') && !p.isReversed ? (
                        <IconButton
                          label={t('Reverse payment {{number}}', { number: p.receiptNumber })}
                          icon={<Undo2 />}
                          onClick={() => setReversing(p)}
                        />
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardBody className="space-y-4">
              <div>
                <p className="text-xs font-medium tracking-wide text-muted uppercase">{t('Outstanding')}</p>
                <p className="mt-1 text-3xl font-semibold tracking-tight text-ink tabular">
                  {money(inv.outstanding)}
                </p>
              </div>
              <div>
                <div
                  className="h-2 overflow-hidden rounded-full bg-surface-sunken"
                  role="progressbar"
                  aria-label={t('Share of the invoice paid')}
                  aria-valuenow={Math.round(paidPct)}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <div className="h-full rounded-full bg-success-500" style={{ width: `${paidPct}%` }} />
                </div>
                <p className="mt-1.5 text-xs text-muted">
                  {t('{{paid}} paid of {{total}}', { paid: money(inv.amountPaid), total: money(inv.total) })}
                </p>
              </div>
            </CardBody>
          </Card>
          <Card>
            <CardHeader title={t('Details')} />
            <CardBody>
              <DescriptionList
                className="sm:grid-cols-1"
                items={[
                  {
                    label: t('Student'),
                    value: (
                      <Link
                        to={`/admin/finance/students/${inv.student.id}/ledger`}
                        className="font-medium text-brand-700 hover:underline"
                      >
                        {inv.student.name}
                      </Link>
                    ),
                  },
                  { label: t('Class'), value: inv.student.className ?? '—' },
                  {
                    label: t('Student code'),
                    value: <span className="font-mono text-xs">{inv.student.studentCode}</span>,
                  },
                  { label: t('Issued'), value: formatDate(inv.issueDate) },
                  {
                    label: t('Due'),
                    value: (
                      <span className={cn(inv.isOverdue && 'font-medium text-danger-700')}>
                        {formatDate(inv.dueDate)}
                      </span>
                    ),
                  },
                  { label: t('Currency'), value: inv.currency },
                ]}
              />
            </CardBody>
          </Card>
        </div>
      </div>

      {paying ? <RecordPaymentDialog open onOpenChange={setPaying} invoice={inv} /> : null}
      {cancelling ? (
        <ReasonDialog
          open
          onOpenChange={setCancelling}
          title={t('Cancel {{number}}?', { number: inv.number })}
          description={t('The invoice stays on record as cancelled and its fees can be billed again.')}
          submitLabel={t('Cancel invoice')}
          pending={cancel.isPending}
          onSubmit={async (reason) => {
            await cancel.mutateAsync({ id: inv.id, reason })
            toast.success(t('Invoice cancelled'), inv.number)
            setCancelling(false)
          }}
        />
      ) : null}
      {reversing ? (
        <ReasonDialog
          open
          onOpenChange={(o) => (o ? undefined : setReversing(null))}
          title={t('Reverse {{number}}?', { number: reversing.receiptNumber })}
          description={t(
            '{{amount}} will be added back to the outstanding balance. The receipt stays on record as reversed.',
            { amount: money(reversing.amount) },
          )}
          submitLabel={t('Reverse payment')}
          pending={reverse.isPending}
          onSubmit={async (reason) => {
            await reverse.mutateAsync({ id: reversing.id, reason })
            toast.success(t('Payment reversed'), reversing.receiptNumber)
            setReversing(null)
          }}
        />
      ) : null}
    </>
  )
}
